export type SessionStatus = 'starting' | 'ready' | 'error' | 'stopped'

export type AceStat = {
  status: string
  peers: number
  speedDown: number
  speedUp: number
  downloaded: number
  uploaded: number
  progress: number
  raw: Record<string, unknown>
}

export type FfmpegProgress = {
  /** 1.0 = keeping up with real time. Below ~0.97 sustained means trouble. */
  speed: number
  dropped: number
  duplicated: number
  outTimeSeconds: number
  updatedAt: number
}

/** Measured from the written HLS segments; ffmpeg cannot report it with -c copy. */
export type OutputStats = {
  bitrateKbps: number
  segments: number
  bytesEmitted: number
  segmentSeconds: number
}

export type MediaInfo = {
  videoCodec: string | null
  videoProfile: string | null
  width: number | null
  height: number | null
  fps: number | null
  audioCodec: string | null
  audioChannels: number | null
  audioSampleRate: number | null
  chromecastSafe: boolean
  chromecastNote: string | null
}

/** Measured in the browser, not reported by the server. */
export type PlaybackStats = {
  bufferSeconds: number
  droppedFrames: number
  totalFrames: number
  bandwidthEstimate: number
  stalls: number
  playing: boolean
}

export type Session = {
  id: string
  link: string
  target: string
  status: SessionStatus
  error: string | null
  hlsUrl: string
  hlsPath: string
  createdAt: number
  readyAt: number | null
  isLive: boolean
  castUrl: string
  /** 'transcode' when the feed had to be re-encoded on the GPU for the TV. */
  videoMode: 'copy' | 'transcode'
  /** Progress of the GPU rendition built for the TV when the feed has no IDR. */
  tv: 'none' | 'checking' | 'starting' | 'ready' | 'failed'
  stat: AceStat | null
  progress: FfmpegProgress | null
  output: OutputStats | null
  media: MediaInfo | null
  castDeviceId: string | null
  clients: ClientAccess[]
  ffmpegLog: string[]
}

export type ClientAccess = {
  ip: string
  requests: number
  /** 404s. On a Chromecast these are fatal and unrecoverable. */
  misses: number
  lastAt: number
}

export type CastDevice = {
  id: string
  name: string
  model: string | null
  address: string
  port: number
  hasVideo: boolean
  /** Verified by a TCP probe on refresh; null when not probed yet. */
  reachable: boolean | null
}

export type MatchState = 'pre' | 'in' | 'post'

export type Match = {
  id: string
  kickoff: string
  competition: string
  competitionSlug: string
  home: string
  away: string
  atHome: boolean
  opponent: string
  venue: string | null
  state: MatchState
  timeConfirmed: boolean
  link: string | null
  /** Broadcaster: chosen here, published by the listing, or a blanket default. */
  platform: string | null
  platformSource: 'manual' | 'auto' | 'default' | null
}

export type Schedule = {
  team: string
  notifyMinutesBefore: number
  platformOptions: string[]
  broadcast: { lastRefresh: number; lastError: string | null; count: number }
  lastRefresh: number
  lastError: string | null
  count: number
  matches: Match[]
}

export type ChannelEntry = {
  id: string
  channel: string | null
  ok: number
  ko: number
  addedAt: string | null
  okAt: string | null
  koAt: string | null
  score: number
}

export type ChannelSnapshot = {
  slug: string
  name: string | null
  fetchedAt: number
  error: string | null
  entries: ChannelEntry[]
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    // The session lives in an HttpOnly cookie.
    credentials: 'same-origin',
    headers: {
      // Only when there is something to declare: Fastify rejects a request
      // that claims a JSON body and sends none, which silently broke every
      // bodyless POST (logout, refresh, push test).
      ...(init?.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(init?.headers ?? {}),
    },
  })
  const text = await res.text()
  const body = text ? (JSON.parse(text) as unknown) : null

  if (!res.ok) {
    const message =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: unknown }).error)
        : `${res.status} ${res.statusText}`
    throw new ApiError(message, res.status)
  }
  return body as T
}

export const api = {
  me: () => request<{ authRequired: boolean; authenticated: boolean }>('/api/auth/me'),

  login: (password: string) =>
    request<{ ok: true }>('/api/auth/login', { method: 'POST', body: JSON.stringify({ password }) }),

  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

  getChannels: () =>
    request<{ recentHours: number; channels: Array<{ slug: string; name: string | null }> }>('/api/channels'),

  getChannel: (slug: string, refresh = false) =>
    request<ChannelSnapshot>(`/api/channels/${encodeURIComponent(slug)}${refresh ? '?refresh=1' : ''}`),

  getSchedule: () => request<Schedule>('/api/schedule'),

  refreshSchedule: () => request<{ lastRefresh: number; lastError: string | null; count: number }>(
    '/api/schedule/refresh',
    { method: 'POST' },
  ),

  setMatchLink: (id: string, link: string | null) =>
    request<{ ok: true; link: string | null }>(`/api/schedule/${encodeURIComponent(id)}/link`, {
      method: 'PUT',
      body: JSON.stringify({ link }),
    }),

  setMatchPlatform: (id: string, platform: string | null) =>
    request<{ ok: true; platform: string | null }>(
      `/api/schedule/${encodeURIComponent(id)}/platform`,
      { method: 'PUT', body: JSON.stringify({ platform }) },
    ),

  getPushKey: () => request<{ publicKey: string | null; subscribers: number }>('/api/push/key'),

  pushSubscribe: (subscription: unknown) =>
    request<{ ok: true; subscribers: number }>('/api/push/subscribe', {
      method: 'POST',
      body: JSON.stringify(subscription),
    }),

  pushUnsubscribe: (endpoint: string) =>
    request<{ ok: true; subscribers: number }>('/api/push/unsubscribe', {
      method: 'POST',
      body: JSON.stringify({ endpoint }),
    }),

  pushTest: () =>
    request<{ sent: number; dropped: number; errors: string[] }>('/api/push/test', { method: 'POST' }),

  createSession: (link: string) =>
    request<Session>('/api/sessions', { method: 'POST', body: JSON.stringify({ link }) }),

  getSession: (id: string) => request<Session>(`/api/sessions/${id}`),

  stopSession: (id: string) => request<{ ok: true }>(`/api/sessions/${id}`, { method: 'DELETE' }),

  listDevices: (refresh = false) =>
    request<{ devices: CastDevice[] }>(`/api/cast/devices${refresh ? '?refresh=1' : ''}`),

  castPlay: (deviceId: string, sessionId: string) =>
    request<{ ok: true; hlsUrl: string; volume: { level?: number; muted?: boolean } | null }>('/api/cast/play', {
      method: 'POST',
      body: JSON.stringify({ deviceId, sessionId }),
    }),

  castStop: (deviceId: string, sessionId?: string) =>
    request<{ ok: true }>('/api/cast/stop', { method: 'POST', body: JSON.stringify({ deviceId, sessionId }) }),

  castVolume: (deviceId: string, level: number) =>
    request<{ ok: true }>('/api/cast/volume', { method: 'POST', body: JSON.stringify({ deviceId, level }) }),

  castStatus: (deviceId: string) =>
    request<{
      status: unknown
      volume: { level?: number; muted?: boolean } | null
      report: { playerState: string | null; idleReason: string | null } | null
    }>(`/api/cast/status?deviceId=${encodeURIComponent(deviceId)}`),
}

export function formatBitrate(bytesPerSecond: number): string {
  const kbps = bytesPerSecond * 8
  if (kbps >= 1000) return `${(kbps / 1000).toFixed(1)} Mbps`
  return `${Math.round(kbps)} kbps`
}

export function formatBytes(kilobytes: number): string {
  if (kilobytes >= 1024 * 1024) return `${(kilobytes / 1024 / 1024).toFixed(2)} GB`
  if (kilobytes >= 1024) return `${(kilobytes / 1024).toFixed(1)} MB`
  return `${Math.round(kilobytes)} KB`
}
